import * as React from "react";
import { EXCLUDED_BARCODE_VALUES } from "../../../helpers/excludedBarcodeValues";
import { markCompositeInventoryOrderCartonAsReceivedMutation } from "../../../graph";
import { playAudioForSuccess } from "../../../helpers/playAudioForSuccess";
import { useRouter } from "found";
import BarcodeReader from "react-barcode-reader";
import BulkReceivingDetailView from "./components/BulkReceivingDetailView";
import Grid from "@mui/material/Grid";
import Paper from "@mui/material/Paper";
import TabbedAppBar from "../../../components/TabbedAppBar";
import Typography from "@mui/material/Typography";
import withSnackbar from "../../../components/withSnackbar";

import { useEffect } from "react";
import { useMutation } from "@apollo/client";
import { useState } from "react";
import ResponsiveDrawer from "../../../components/ResponsiveDrawer";
import withBarcodePivot from "../../../components/BarcodePivot/withBarcodePivot";

const BulkReceivingApp = ({
  appBarBackgroundColor,
  backUrl,
  setGlobalBarcodeReaderEnabled,
  showErrorSnackbar,
  showSuccessSnackbar,
  showWarningSnackbar
}) => {
  const { router } = useRouter();

  useEffect(() => {
    setGlobalBarcodeReaderEnabled(false);
    return () => setGlobalBarcodeReaderEnabled(true);
  }, [setGlobalBarcodeReaderEnabled]);

  const autoPrint = false;

  // States:
  const [cartonData, setCartonData] = useState([]);
  const [drawerDisplayState, setDrawerDisplayState] = useState("closed");

  // Mutations:
  const [markCompositeInventoryOrderCartonAsReceived] = useMutation(
    markCompositeInventoryOrderCartonAsReceivedMutation
  );

  // Handlers:
  const handleAppHeaderRequestBack = () =>
    router.push(backUrl ? backUrl : "/apps");

  const handleCartonIdentifierInput = cartonIdentifier => {
    markCompositeInventoryOrderCartonAsReceived({
      variables: { inventoryOrderCartonIdentifier: cartonIdentifier }
    })
      .then(
        ({
          data: {
            markCompositeInventoryOrderCartonAsReceived: {
              succeeded,
              updatedInventoryOrderCartons,
              errorStatus
            }
          }
        }) => {
          if (succeeded) {
            showSuccessSnackbar("Bulk carton received.");
            playAudioForSuccess();

            let data = updatedInventoryOrderCartons.reduce(
              (acc, updatedCarton) => {
                let productionJobId = updatedCarton.orderProductionJob.id;
                if (!acc[productionJobId]) {
                  acc[productionJobId] = updatedCarton.orderProductionJob;
                }

                let productionJob = acc[productionJobId];
                if (!productionJob.lineItemsByGtin) {
                  productionJob.lineItemsByGtin =
                    productionJob.lineItemBreakdownByProduct.reduce(
                      (productAcc, lineItemBreakdownByProduct) => {
                        return lineItemBreakdownByProduct.lineItems.reduce(
                          (colorAcc, lineItem) => {
                            return lineItem.sizes.reduce(
                              (sizeAcc, sizeItem) => {
                                if (!sizeAcc[sizeItem.gtin]) {
                                  sizeAcc[sizeItem.gtin] = {
                                    totalQuantity: 0,
                                    cartonQuantity: 0
                                  }; //size: sizeItem.size, color: lineItem.color, product: lineItemBreakdownByProduct.product
                                }
                                sizeAcc[sizeItem.gtin].totalQuantity =
                                  sizeAcc[sizeItem.gtin].totalQuantity +
                                  sizeItem.quantity;
                                return sizeAcc;
                              },
                              colorAcc
                            );
                          },
                          productAcc
                        );
                      },
                      {}
                    );
                }

                if (
                  !productionJob.lineItemsByGtin[
                    updatedCarton.inventoryOrderItem.gtin
                  ]
                ) {
                  console.error(
                    `Unable to match GTIN to order: ${updatedCarton.inventoryOrderItem.gtin}`
                  );
                  return acc;
                }

                // set quantity from scanned carton
                productionJob.lineItemsByGtin[
                  updatedCarton.inventoryOrderItem.gtin
                ].cartonQuantity =
                  productionJob.lineItemsByGtin[
                    updatedCarton.inventoryOrderItem.gtin
                  ].cartonQuantity + updatedCarton.quantity;
                return acc;
              },
              {}
            );

            // convert to array
            data = Object.entries(data).map(([, value]) => value);
            setCartonData(data);
            setDrawerDisplayState(
              data.length > 1 ? "open-full" : "open-partial"
            );
          } else if (errorStatus === "INVENTORY_ORDER_CARTON_NOT_FOUND") {
            showWarningSnackbar(
              "No Carton found. Please lookup the order manually and add a box."
            );
          } else if (errorStatus === "NON_COMPOSITE_CARTON_UNSUPPORTED") {
            showWarningSnackbar(
              "Non-Bulk Carton. Delivery carton back to receiving desk."
            );
          } else {
            showErrorSnackbar(
              `No carton with identifier ${cartonIdentifier} exists.`
            );
          }
        }
      )
      .catch(e => {
        showErrorSnackbar(e.message);
      });
  };

  const handleSearch = searchQuery => handleCartonIdentifierInput(searchQuery);

  const handleBarcodeError = () => showErrorSnackbar("Error scanning barcode");

  const handleBarcodeScan = barcode => {
    console.log("BulkReceivingApp Barcode Scan");
    if (EXCLUDED_BARCODE_VALUES.includes(barcode)) {
      showErrorSnackbar("This barcode is excluded from processing");
      return;
    }
    handleCartonIdentifierInput(barcode);
  };

  return (
    <>
      <div>
        <TabbedAppBar
          title="Bulk Receiving"
          onRequestBack={handleAppHeaderRequestBack}
          onSearch={handleSearch}
          appBarBackgroundColor={appBarBackgroundColor}
        />
        <div>
          <BarcodeReader
            onError={handleBarcodeError}
            onScan={handleBarcodeScan}
          />
          <Typography
            variant="h6"
            align="center"
            color="inherit"
            style={{ flexGrow: 1, marginTop: "10em" }}
          >
            Scan bulk carton to receive
          </Typography>
        </div>
      </div>
      <ResponsiveDrawer
        displayState={drawerDisplayState}
        onRequestClose={() => {
          setDrawerDisplayState("closed");
          setCartonData([]);
        }}
        title="Carton Contents"
      >
        {cartonData ? (
          <React.Fragment>
            <Grid container>
              {cartonData &&
                cartonData.map((data, index) => (
                  <Grid key={index} size={{ xs: 2, sm: 4, md: 4 }}>
                    <Paper
                      style={{
                        padding: 10,
                        margin: 10,
                        textAlign: "center"
                      }}
                    >
                      <BulkReceivingDetailView
                        autoPrint={autoPrint}
                        cartonProductionJob={data}
                      />
                    </Paper>
                  </Grid>
                ))}
            </Grid>
          </React.Fragment>
        ) : null}
      </ResponsiveDrawer>
    </>
  );
};

export default withBarcodePivot(withSnackbar(BulkReceivingApp));
